import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { useTheme } from '../lib/useTheme';
import { useTracker } from '../lib/useTracker';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { COURSE_STRUCTURE, FORMS_31, CATEGORIES, VIDEOS } from '../lib/tuRadarData';
import VideoPlayer from '../components/radar/VideoPlayer';
import mbtiQuestions from '../data/mbtiQuestions';
import skillsQuestions from '../data/skillsQuestions';
import radarSchedule from '../data/radarSchedule';
import thoughtPrompts from '../data/thoughtPrompts';
import { calculateMBTI, calculateRadar, suggestRoute, ROUTE_LABELS, SKILL_LABELS, calculateFormMatch } from '../lib/testCalculations';
import FormaCard from '../components/radar/FormaCard';
import MyShortlist from '../components/radar/MyShortlist';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', verde:'#1D9E75' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const glass = { background:'rgba(255,255,255,0.05)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16 };

// Calculate current day in course (1-based, from purchased_at)
function getCurrentDay(purchasedAt) {
  if (!purchasedAt) return 1;
  const start = new Date(purchasedAt);
  const now = new Date();
  const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24)) + 1;
  return Math.min(Math.max(diff, 1), 30);
}

function getAllDays() {
  const weeks = [COURSE_STRUCTURE.week1, COURSE_STRUCTURE.week2, COURSE_STRUCTURE.week3, COURSE_STRUCTURE.week4];
  return weeks.flatMap(w => w.days);
}

export default function EscritorioTuRadar() {
  const { isMobile } = useScreen();
  const t = useTheme();
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  useTracker('escritorio-tu-radar', user?.id);

  const [view, setView] = useState('today');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [testPhase, setTestPhase] = useState('mbti');
  const [testStep, setTestStep] = useState(0);
  const [mbtiAnswers, setMbtiAnswers] = useState([]);
  const [skillsAnswers, setSkillsAnswers] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [savingTest, setSavingTest] = useState(false);
  const [completingDay, setCompletingDay] = useState(false);
  const [thoughtCounts, setThoughtCounts] = useState({ identidad:0, vision:0, miedo:0, accion:0, skill:0 });
  const [dbForms, setDbForms] = useState([]);
  const [userChoices, setUserChoices] = useState([]);
  const testCompleted = !!profile?.test_completed_at;

  const radarDay = profile?.radar_current_day || 1;
  const completedDays = profile?.radar_completed_days || [];
  const todaySchedule = radarSchedule.find(d => d.day === radarDay) || radarSchedule[0];

  // Legacy compat
  const currentDay = radarDay;
  const allDays = getAllDays();
  const todayContent = allDays.find(d => d.day === currentDay) || allDays[0];

  // Load forms + user choices from Supabase
  useEffect(() => {
    supabase.from('forms_31').select('*').order('id').then(({ data }) => { if (data) setDbForms(data); });
  }, []);
  useEffect(() => {
    if (!user) return;
    supabase.from('user_form_choices').select('*').eq('user_id', user.id)
      .then(({ data }) => { if (data) setUserChoices(data); });
  }, [user, view]);

  // Load thought counts
  useEffect(() => {
    if (!user) return;
    supabase.from('thoughts').select('thought_type').eq('user_id', user.id)
      .then(({ data }) => {
        if (!data) return;
        const counts = { identidad:0, vision:0, miedo:0, accion:0, skill:0 };
        data.forEach(t => { if (counts[t.thought_type] !== undefined) counts[t.thought_type]++; });
        setThoughtCounts(counts);
      });
  }, [user, view]);

  const completeDay = async () => {
    if (!user || completingDay) return;
    setCompletingDay(true);
    const newCompleted = [...completedDays, radarDay];
    const newDay = Math.min(radarDay + 1, 30);
    const updates = {
      radar_current_day: newDay,
      radar_completed_days: newCompleted,
      updated_at: new Date().toISOString(),
    };
    if (radarDay === 1 && !profile?.radar_started_at) updates.radar_started_at = new Date().toISOString();
    if (newDay > 30 || radarDay === 30) updates.radar_finished_at = new Date().toISOString();

    await supabase.from('profiles').update(updates).eq('id', user.id);
    if (refreshProfile) await refreshProfile();
    setCompletingDay(false);
  };

  const sidebarItems = [
    { section: '📡 Tu Radar curso', items: [
      { id: 'today', label: 'Mi día de hoy', icon: '📅', locked: false },
      { id: 'radar', label: 'Mi Radar', icon: '📡', locked: false },
      { id: 'forms', label: 'Las 31 formas', icon: '🗺️', locked: false },
      { id: 'videos', label: 'Videos exclusivos', icon: '🎬', locked: false },
      { id: 'decision', label: 'Mi decisión final', icon: '🎯', locked: currentDay < 22 },
    ]},
    { section: '🌐 Comunidad', items: [
      { id: 'thoughts', label: 'Pensamientos', icon: '💭', locked: false },
      { id: 'sala', label: 'La Sala', icon: '🏠', locked: false },
      { id: 'ideas', label: 'Mis Ideas', icon: '🔒', locked: true, upgradeMsg: 'Disponible desde Doer' },
    ]},
    { section: '✨ Genio & Luca', items: [
      { id: 'chat', label: 'Chat del curso', icon: '⚡', locked: false, badge: '10/día' },
    ]},
  ];

  const itemStyle = (item) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', borderRadius: 10, cursor: item.locked ? 'not-allowed' : 'pointer',
    background: view === item.id ? 'rgba(93,193,185,0.08)' : 'transparent',
    border: view === item.id ? '0.5px solid rgba(93,193,185,0.2)' : '0.5px solid transparent',
    opacity: item.locked ? 0.4 : 1,
    transition: 'all 0.2s',
    fontSize: 13, fontFamily: font, color: view === item.id ? C.turquesa : 'rgba(255,255,255,0.7)',
  });

  // ========== VIEWS ==========

  const TYPE_COLORS = { identidad:'#534AB7', vision:'#FDBE02', miedo:'#eb636b', accion:'#1D9E75', skill:'#5dc1b9' };
  const TYPE_LABELS = { identidad:'🪞 Identidad', vision:'🔭 Visión', miedo:'🌊 Miedo', accion:'⚡ Acción', skill:'💎 Habilidad' };

  const renderToday = () => {
    const s = todaySchedule;
    const prompts = s.thoughtType && thoughtPrompts[s.thoughtType]
      ? s.thoughtPrompts.map(i => thoughtPrompts[s.thoughtType][i]).filter(Boolean)
      : [];
    const typeColor = TYPE_COLORS[s.thoughtType] || C.turquesa;
    const formas = s.formasIds ? s.formasIds.map(id => FORMS_31.find(f => f.id === id)).filter(Boolean) : [];
    const isCompleted = completedDays.includes(radarDay);
    const totalThoughts = Object.values(thoughtCounts).reduce((a, b) => a + b, 0);

    return (
      <div>
        {/* Day header */}
        <p style={{ fontSize:11, color:C.mango, fontWeight:500, letterSpacing:1, margin:'0 0 6px' }}>DÍA {radarDay} DE 30 · SEMANA {s.week}</p>
        <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 8px' }}>{s.title}</h2>
        <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', lineHeight:1.7, margin:'0 0 20px' }}>{s.description}</p>

        {/* Progress bar */}
        <div style={{ ...glass, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
            <span style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>Progreso del curso</span>
            <span style={{ fontSize:12, color:C.turquesa, fontWeight:500 }}>{Math.round((radarDay / 30) * 100)}%</span>
          </div>
          <div style={{ height:6, background:'rgba(255,255,255,0.08)', borderRadius:6, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${(radarDay / 30) * 100}%`, background:C.turquesa, borderRadius:6, transition:'width 0.5s ease' }} />
          </div>
        </div>

        {/* Test CTA (day 1 only) */}
        {s.type === 'test' && !testCompleted && (
          <button onClick={() => setView('test')} style={{ padding:'14px 28px', background:C.morado, color:'#fff', border:'none', borderRadius:50, fontSize:15, fontWeight:500, cursor:'pointer', fontFamily:font, marginBottom:20 }}>
            Hacer el test de descubrimiento →
          </button>
        )}

        {/* Video (if day has videoSlot) */}
        {s.videoSlot && (
          <div style={{ marginBottom:20 }}>
            <VideoPlayer
              video={dbVideos.find(v => v.slot === `v${s.videoSlot}`) || { id:s.videoSlot, title:s.title, unlock_day:s.day, duration:'5 min' }}
              userId={user?.id}
              unlocked={true}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Explore formas (if day has formasIds) */}
        {s.formasIds && s.formasIds.length > 0 && (
          <div style={{ marginBottom:20 }}>
            <p style={{ fontSize:13, fontWeight:500, margin:'0 0 10px', color:'rgba(255,255,255,0.7)' }}>Formas a explorar hoy</p>
            {s.formasIds.map(fid => {
              const dbForma = dbForms.find(f => f.id === fid);
              if (dbForma) {
                return (
                  <FormaCard
                    key={fid}
                    forma={dbForma}
                    userRadar={userRadar}
                    userId={user?.id}
                    existingChoice={userChoices.find(c => c.form_id === fid)?.status}
                    onChoiceUpdate={(formId, status) => {
                      setUserChoices(prev => [...prev.filter(c => c.form_id !== formId), { user_id:user?.id, form_id:formId, status }]);
                    }}
                  />
                );
              }
              // Fallback to static data if DB not seeded
              const staticForma = FORMS_31.find(f => f.id === fid);
              if (!staticForma) return null;
              return (
                <div key={fid} style={{ ...glass, padding:'14px 18px', display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                  <span style={{ fontSize:24 }}>{staticForma.emoji}</span>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:14, fontWeight:500, margin:'0 0 2px' }}>{staticForma.name}</p>
                    <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', margin:0 }}>{staticForma.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Thought prompts block */}
        {prompts.length > 0 && (
          <div style={{ marginBottom:20, padding:'24px 20px', borderRadius:16, background:`linear-gradient(135deg, ${typeColor}10, rgba(255,255,255,0.02))`, border:`1px solid ${typeColor}25` }}>
            <p style={{ fontSize:13, fontWeight:500, color:typeColor, margin:'0 0 14px' }}>
              {TYPE_LABELS[s.thoughtType] || s.thoughtType} · Hoy te invitamos a pensar en...
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {prompts.map((prompt, i) => (
                <div key={i} style={{ padding:'12px 16px', borderRadius:12, background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.08)' }}>
                  <p style={{ fontSize:14, color:'rgba(255,255,255,0.8)', lineHeight:1.6, margin:0 }}>{prompt}</p>
                </div>
              ))}
            </div>
            <button onClick={() => navigate(`/pensamientos/nuevo?type=${s.thoughtType}&from_day=${radarDay}`)} style={{
              marginTop:16, padding:'12px 24px', background:typeColor, color:'#fff', border:'none', borderRadius:50,
              fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font, boxShadow:`0 0 20px ${typeColor}30`,
            }}>
              + Crear pensamiento en la Red
            </button>
          </div>
        )}

        {/* Tu red está creciendo */}
        <div style={{ ...glass, padding:'20px', marginBottom:20 }}>
          <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px' }}>🌐 Tu red está creciendo</p>
          <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', margin:'0 0 14px' }}>{totalThoughts} pensamiento{totalThoughts !== 1 ? 's' : ''} publicado{totalThoughts !== 1 ? 's' : ''}</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {Object.entries(TYPE_COLORS).map(([type, color]) => (
              <div key={type} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:50, background:`${color}10`, border:`0.5px solid ${color}25` }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:color }} />
                <span style={{ fontSize:11, color:'rgba(255,255,255,0.6)' }}>{type}: <strong style={{ color }}>{thoughtCounts[type]}</strong></span>
              </div>
            ))}
          </div>
        </div>

        {/* Complete day button */}
        {!isCompleted ? (
          <button onClick={completeDay} disabled={completingDay} style={{
            width:'100%', padding:16, fontSize:15, fontWeight:500, borderRadius:50, cursor:'pointer', fontFamily:font,
            background:C.turquesa, color:'#fff', border:'none', opacity:completingDay ? 0.6 : 1,
            boxShadow:'0 0 20px rgba(93,193,185,0.3)',
          }}>
            {completingDay ? 'Guardando...' : `Día ${radarDay} completado ✓`}
          </button>
        ) : (
          <div style={{ textAlign:'center', padding:'12px', borderRadius:12, background:'rgba(93,193,185,0.08)', border:'0.5px solid rgba(93,193,185,0.2)' }}>
            <p style={{ fontSize:14, color:C.turquesa, margin:0, fontWeight:500 }}>✓ Día {radarDay} completado</p>
          </div>
        )}

        {/* CTA to Arbol after course progress */}
        {radarDay >= 15 && (
          <div style={{ marginTop: 20, padding: '18px 20px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(93,193,185,0.08), rgba(253,190,2,0.06))', border: '1px solid rgba(93,193,185,0.2)', textAlign: 'center' }}>
            <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>🌳 Tu negocio tiene un arbol de misiones</p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 0 12px', lineHeight: 1.5 }}>Construye tu empresa mision a mision. Desbloquea mundos, gana insignias y accede a crowdfunding.</p>
            <button onClick={() => navigate('/arbol')} style={{
              padding: '10px 24px', background: C.turquesa, color: '#fff', border: 'none',
              borderRadius: 50, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: font,
            }}>
              Ir a Mi Arbol →
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderRadar = () => {
    const radar = testResult?.radar || profile?.radar_skills;
    const mbti = testResult?.mbti || profile?.mbti_type;
    const route = testResult?.route || (profile?.suggested_route ? { primary: profile.suggested_route } : null);

    if (!testCompleted && !testResult) {
      return (
        <div>
          <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 8px' }}>Mi Radar</h2>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', margin:'0 0 24px' }}>Completa el test del Día 1 para ver tu Radar.</p>
          <button onClick={() => setView('test')} style={{ padding:'14px 28px', background:C.morado, color:'#fff', border:'none', borderRadius:50, fontSize:15, fontWeight:500, cursor:'pointer', fontFamily:font }}>
            Hacer el test →
          </button>
        </div>
      );
    }

    const routeInfo = route ? ROUTE_LABELS[route.primary] : null;

    return (
      <div>
        <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 8px' }}>Mi Radar</h2>

        {/* MBTI Result */}
        {mbti && (
          <div style={{ ...glass, padding:'20px', marginBottom:16, display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:56, height:56, borderRadius:14, background:'rgba(83,74,183,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:20, fontWeight:600, color:C.morado, fontFamily:font }}>{mbti}</span>
            </div>
            <div>
              <p style={{ fontSize:14, fontWeight:500, margin:'0 0 2px' }}>Tu tipo MBTI</p>
              <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', margin:0 }}>Basado en tus 16 respuestas de personalidad</p>
            </div>
          </div>
        )}

        {/* Route Suggestion */}
        {routeInfo && (
          <div style={{ ...glass, padding:'20px', marginBottom:16, borderLeft:`3px solid ${C.mango}` }}>
            <p style={{ fontSize:11, color:C.mango, fontWeight:500, letterSpacing:1, margin:'0 0 6px' }}>TU RUTA SUGERIDA</p>
            <p style={{ fontSize:18, fontWeight:500, margin:'0 0 4px' }}>{routeInfo.emoji} {routeInfo.name}</p>
            <p style={{ fontSize:13, color:'rgba(255,255,255,0.6)', lineHeight:1.6, margin:0 }}>{routeInfo.desc}</p>
          </div>
        )}

        {/* Radar Chart (SVG) */}
        {radar && (
          <div style={{ ...glass, padding:'24px', marginBottom:16 }}>
            <p style={{ fontSize:14, fontWeight:500, margin:'0 0 16px' }}>Tu mapa de habilidades</p>
            <svg viewBox="0 0 300 300" style={{ width:'100%', maxWidth:360, margin:'0 auto', display:'block' }}>
              {/* Background circles */}
              {[1,2,3,4,5].map(r => (
                <circle key={r} cx="150" cy="150" r={r * 26} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
              ))}
              {/* Skill axes + labels */}
              {Object.keys(SKILL_LABELS).map((key, i) => {
                const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
                const lx = 150 + Math.cos(angle) * 145;
                const ly = 150 + Math.sin(angle) * 145;
                return (
                  <text key={key} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.45)" fontSize="9" fontFamily={font}>
                    {SKILL_LABELS[key]}
                  </text>
                );
              })}
              {/* Radar polygon */}
              <polygon
                points={Object.keys(SKILL_LABELS).map((key, i) => {
                  const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
                  const val = (radar[key] || 0) / 10;
                  const r = val * 130;
                  return `${150 + Math.cos(angle) * r},${150 + Math.sin(angle) * r}`;
                }).join(' ')}
                fill="rgba(93,193,185,0.15)"
                stroke={C.turquesa}
                strokeWidth="1.5"
              />
              {/* Dots on vertices */}
              {Object.keys(SKILL_LABELS).map((key, i) => {
                const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
                const val = (radar[key] || 0) / 10;
                const r = val * 130;
                return (
                  <circle key={key} cx={150 + Math.cos(angle) * r} cy={150 + Math.sin(angle) * r} r="3" fill={C.turquesa} />
                );
              })}
            </svg>
            {/* Score list */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:16, justifyContent:'center' }}>
              {Object.entries(SKILL_LABELS).map(([key, label]) => (
                <div key={key} style={{ padding:'6px 12px', borderRadius:50, background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.08)', fontSize:11, color:'rgba(255,255,255,0.7)' }}>
                  {label}: <span style={{ color:C.turquesa, fontWeight:600 }}>{radar[key] || 0}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const userRadar = testResult?.radar || profile?.radar_skills || null;

  const renderForms = () => {
    const formsToShow = dbForms.length > 0 ? dbForms : [];
    const handleChoiceUpdate = (formId, status) => {
      setUserChoices(prev => {
        const filtered = prev.filter(c => c.form_id !== formId);
        return [...filtered, { user_id: user?.id, form_id: formId, status }];
      });
    };
    return (
      <div>
        <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 8px' }}>Las 31 formas</h2>
        <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', margin:'0 0 24px' }}>31 formas reales de ganar dinero en internet. Evalúa cada una.</p>
        {formsToShow.length > 0 ? (
          formsToShow.map(f => (
            <FormaCard
              key={f.id}
              forma={f}
              userRadar={userRadar}
              userId={user?.id}
              existingChoice={userChoices.find(c => c.form_id === f.id)?.status}
              onChoiceUpdate={handleChoiceUpdate}
            />
          ))
        ) : (
          <div style={{ ...glass, padding:'40px 20px', textAlign:'center' }}>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', margin:0 }}>
              Las 31 formas se cargan desde la base de datos. Ejecuta el SQL de seed para verlas.
            </p>
          </div>
        )}
      </div>
    );
  };

  const [dbVideos, setDbVideos] = useState([]);
  useEffect(() => {
    supabase.from('radar_videos').select('*').order('sort_order')
      .then(({ data }) => { if (data) setDbVideos(data); });
  }, []);

  const renderVideos = () => {
    const videosToShow = dbVideos.length > 0 ? dbVideos : VIDEOS.map((v, i) => ({ id: i + 1, slot: v.id, title: v.title, description: '', youtube_id: null, youtube_url: null, unlock_day: v.day, duration: v.duration, sort_order: i + 1 }));
    return (
      <div>
        <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 24px' }}>Videos exclusivos</h2>
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          {videosToShow.map(v => (
            <VideoPlayer
              key={v.id}
              video={v}
              userId={user?.id}
              unlocked={currentDay >= v.unlock_day}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderDecision = () => {
    if (currentDay < 22) {
      return (
        <div style={{ ...glass, padding:'40px 20px', textAlign:'center' }}>
          <p style={{ fontSize:48, margin:'0 0 12px' }}>🔒</p>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', margin:0 }}>
            Se desbloquea el día 22. Hoy es el día {currentDay}.
          </p>
        </div>
      );
    }
    return (
      <MyShortlist
        forms={dbForms}
        choices={userChoices}
        userRadar={userRadar}
      />
    );
  };

  const saveTestResults = async (mbti, radar, route) => {
    if (!user || savingTest) return;
    setSavingTest(true);
    const { error } = await supabase.from('profiles').update({
      mbti_type: mbti,
      radar_skills: radar,
      suggested_route: route.primary,
      test_completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    if (error) console.error('[Test] Save error:', error.message);
    setSavingTest(false);
  };

  const finishTest = async () => {
    const mbti = calculateMBTI(mbtiAnswers);
    const radar = calculateRadar(skillsAnswers);
    const route = suggestRoute(mbti, radar);
    setTestResult({ mbti, radar, route });
    setTestPhase('done');
    await saveTestResults(mbti, radar, route);
  };

  const totalTestQuestions = mbtiQuestions.length + skillsQuestions.length;
  const currentTestIndex = testPhase === 'mbti' ? testStep : mbtiQuestions.length + testStep;

  const renderTest = () => {
    // Already completed — redirect to radar
    if (testCompleted && !testResult) {
      return (
        <div style={{ textAlign:'center', padding:'2rem 0' }}>
          <p style={{ fontSize:48, margin:'0 0 12px' }}>✅</p>
          <p style={{ fontFamily:serif, fontSize:22, margin:'0 0 8px' }}>Test ya completado</p>
          <button onClick={() => setView('radar')} style={{ padding:'12px 28px', background:C.morado, color:'#fff', border:'none', borderRadius:50, fontSize:14, cursor:'pointer', fontFamily:font }}>Ver mi Radar →</button>
        </div>
      );
    }

    // Done — show summary
    if (testPhase === 'done' && testResult) {
      const routeInfo = ROUTE_LABELS[testResult.route.primary];
      return (
        <div style={{ textAlign:'center', padding:'1rem 0', animation:'fadeIn 0.4s ease' }}>
          <p style={{ fontSize:48, margin:'0 0 12px' }}>🎉</p>
          <p style={{ fontFamily:serif, fontSize:22, margin:'0 0 6px' }}>Tu Radar está listo</p>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', margin:'0 0 20px' }}>Tipo {testResult.mbti} · Ruta: {routeInfo?.name}</p>
          <button onClick={() => setView('radar')} style={{ padding:'14px 32px', background:C.morado, color:'#fff', border:'none', borderRadius:50, fontSize:15, fontWeight:500, cursor:'pointer', fontFamily:font }}>
            Ver mi Radar completo →
          </button>
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <h2 style={{ fontFamily:serif, fontSize:isMobile?20:24, fontWeight:400, margin:0 }}>
            {testPhase === 'mbti' ? '🧠 Personalidad' : '💪 Habilidades'}
          </h2>
          <span style={{ fontSize:12, color:'rgba(255,255,255,0.4)' }}>{currentTestIndex + 1} / {totalTestQuestions}</span>
        </div>
        <p style={{ fontSize:11, color:testPhase === 'mbti' ? C.morado : C.turquesa, margin:'0 0 16px', fontWeight:500 }}>
          {testPhase === 'mbti' ? `Parte 1: Pregunta ${testStep + 1} de ${mbtiQuestions.length}` : `Parte 2: Pregunta ${testStep + 1} de ${skillsQuestions.length}`}
        </p>

        {/* Progress */}
        <div style={{ height:3, background:'rgba(255,255,255,0.08)', borderRadius:3, marginBottom:24, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${((currentTestIndex + 1) / totalTestQuestions) * 100}%`, background:testPhase === 'mbti' ? C.morado : C.turquesa, borderRadius:3, transition:'width 0.3s ease' }} />
        </div>

        {/* MBTI Phase */}
        {testPhase === 'mbti' && testStep < mbtiQuestions.length && (
          <div style={{ animation:'fadeIn 0.3s ease' }}>
            <p style={{ fontSize:isMobile?16:18, fontWeight:500, margin:'0 0 20px', lineHeight:1.4 }}>{mbtiQuestions[testStep].question}</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {mbtiQuestions[testStep].options.map((opt, i) => (
                <div key={i} onClick={() => {
                  const answer = { questionId: mbtiQuestions[testStep].id, value: opt.value };
                  setMbtiAnswers(prev => [...prev, answer]);
                  if (testStep < mbtiQuestions.length - 1) {
                    setTimeout(() => setTestStep(s => s + 1), 250);
                  } else {
                    // Move to skills phase
                    setTimeout(() => { setTestPhase('skills'); setTestStep(0); }, 400);
                  }
                }} style={{
                  padding:'16px 18px', borderRadius:12, cursor:'pointer', fontSize:14, lineHeight:1.5,
                  border:'0.5px solid rgba(255,255,255,0.1)', background:'transparent', transition:'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.morado; e.currentTarget.style.background = 'rgba(83,74,183,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontWeight:600, color:C.morado, marginRight:8 }}>{opt.value === mbtiQuestions[testStep].options[0].value ? 'A' : 'B'}</span>
                  {opt.text}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills Phase */}
        {testPhase === 'skills' && testStep < skillsQuestions.length && (
          <div style={{ animation:'fadeIn 0.3s ease' }}>
            <p style={{ fontSize:isMobile?16:18, fontWeight:500, margin:'0 0 8px', lineHeight:1.4 }}>{skillsQuestions[testStep].question}</p>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.4)', margin:'0 0 20px' }}>1 = Nada · 5 = Mucho</p>
            <div style={{ display:'flex', justifyContent:'center', gap:isMobile?10:14 }}>
              {[1,2,3,4,5].map(val => (
                <button key={val} onClick={() => {
                  const answer = { questionId: skillsQuestions[testStep].id, skill: skillsQuestions[testStep].skill, value: val };
                  setSkillsAnswers(prev => [...prev, answer]);
                  if (testStep < skillsQuestions.length - 1) {
                    setTimeout(() => setTestStep(s => s + 1), 250);
                  } else {
                    setTimeout(() => finishTest(), 400);
                  }
                }} style={{
                  width:isMobile?48:56, height:isMobile?48:56, borderRadius:'50%', fontSize:18, fontWeight:600,
                  border:'1px solid rgba(255,255,255,0.12)', background:'transparent', color:'#fff',
                  cursor:'pointer', transition:'all 0.2s', fontFamily:font,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(93,193,185,0.12)'; e.currentTarget.style.borderColor = C.turquesa; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                >
                  {val}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
              <span style={{ fontSize:10, color:'rgba(255,255,255,0.3)' }}>Nada</span>
              <span style={{ fontSize:10, color:'rgba(255,255,255,0.3)' }}>Mucho</span>
            </div>
          </div>
        )}

        <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
      </div>
    );
  };

  const renderPlaceholder = (title, msg) => (
    <div>
      <h2 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 16px' }}>{title}</h2>
      <div style={{ ...glass, padding:'40px 20px', textAlign:'center' }}>
        <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', margin:0, fontStyle:'italic' }}>{msg}</p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (view) {
      case 'today': return renderToday();
      case 'radar': return renderRadar();
      case 'forms': return renderForms();
      case 'videos': return renderVideos();
      case 'decision': return renderDecision();
      case 'test': return renderTest();
      case 'thoughts': return renderPlaceholder('Pensamientos', 'Tus pensamientos públicos de la red. Placeholder.');
      case 'sala': return renderPlaceholder('La Sala', 'Chat de la comunidad. Placeholder — redirigir a /sala.');
      case 'ideas': return renderPlaceholder('Mis Ideas', 'Disponible desde Doer. Upgrade para desbloquear.');
      case 'chat': return renderPlaceholder('Chat con Genio & Luca', 'Chat contextual del curso. 10 mensajes/día. Placeholder.');
      default: return renderToday();
    }
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:t.bg, color:'#fff', fontFamily:font }}>
      {/* SIDEBAR */}
      {(sidebarOpen || !isMobile) && (
        <aside style={{
          width: isMobile ? '100%' : 260,
          position: isMobile ? 'fixed' : 'sticky',
          top: 0, left: 0, height: '100vh',
          background: 'rgba(10,10,15,0.97)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          padding: '20px 14px',
          overflowY: 'auto',
          zIndex: isMobile ? 100 : 1,
          backdropFilter: 'blur(20px)',
        }}>
          {/* Logo + close on mobile */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:'#fff', fontSize:10, fontWeight:600 }}>T&D</span>
              </div>
              <span style={{ fontSize:13, fontWeight:500 }}>Tu Radar</span>
            </div>
            {isMobile && (
              <button onClick={() => setSidebarOpen(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.5)', fontSize:20, cursor:'pointer' }}>✕</button>
            )}
          </div>

          {/* Day indicator */}
          <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(253,190,2,0.08)', border:'0.5px solid rgba(253,190,2,0.2)', marginBottom:20 }}>
            <p style={{ fontSize:11, color:C.mango, fontWeight:500, margin:'0 0 2px' }}>DÍA {currentDay} DE 30</p>
            <div style={{ height:3, background:'rgba(255,255,255,0.08)', borderRadius:3, marginTop:6 }}>
              <div style={{ height:'100%', width:`${(currentDay / 30) * 100}%`, background:C.mango, borderRadius:3 }} />
            </div>
          </div>

          {/* Nav sections */}
          {sidebarItems.map((sec, si) => (
            <div key={si} style={{ marginBottom:20 }}>
              <p style={{ fontSize:11, color:'rgba(255,255,255,0.35)', fontWeight:500, letterSpacing:0.5, margin:'0 0 8px', padding:'0 14px' }}>{sec.section}</p>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                {sec.items.map(item => (
                  <div key={item.id} onClick={() => { if (!item.locked) { setView(item.id); if (isMobile) setSidebarOpen(false); } }} style={itemStyle(item)}>
                    <span style={{ fontSize:16 }}>{item.icon}</span>
                    <span style={{ flex:1 }}>{item.label}</span>
                    {item.locked && <span style={{ fontSize:10 }}>🔒</span>}
                    {item.badge && <span style={{ fontSize:9, padding:'2px 6px', borderRadius:50, background:'rgba(93,193,185,0.12)', color:C.turquesa }}>{item.badge}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </aside>
      )}

      {/* MAIN CONTENT */}
      <main style={{ flex:1, padding:isMobile?'1.5rem 1rem':'2rem 2.5rem', maxWidth:800, overflowY:'auto' }}>
        {/* Mobile hamburger */}
        {isMobile && !sidebarOpen && (
          <button onClick={() => setSidebarOpen(true)} style={{ background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'8px 14px', fontSize:13, color:'rgba(255,255,255,0.7)', cursor:'pointer', fontFamily:font, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            ☰ Menú del curso
          </button>
        )}

        {renderContent()}
      </main>
    </div>
  );
}
